/* eslint-disable max-classes-per-file */
import { Amplify } from 'aws-amplify';
import { signOut, signInWithRedirect, fetchAuthSession } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import Helper from 'utils/Helper';
import Logger from 'logger/Logger';
import { getAppConfig } from 'utils/Config';
import { CSRF_HEADER, GetCsrfToken, RemoveCsrfToken, waitForCsrfRefresh, SaveCsrfToken } from 'utils/CsrfToken';
import { getEnvVars } from '../env';

const logger = Logger.getLogger('Auth');

// Track if Amplify has been configured to avoid repeated configuration
let isAmplifyConfigured = false;

// Track the last access token we've seen to detect when it changes (i.e., was refreshed)
let lastKnownAccessToken: string | null = null;

export default interface AuthSession {
  accessToken: string;
}

export interface Headers {
  [key: string]: string;
}

// Options for getting current session
export interface GetSessionOptions {
  forceRefresh?: boolean;
}

// Wrapper around the Authentication methods
interface AuthHandler {
  signOutSession(): Promise<void>;
  signIn(): Promise<void>;
  getCurrentSession(options?: GetSessionOptions): Promise<AuthSession | undefined>;
}

// If we are running as localhost bypass the normal auth
export const getAuthHandler = (): AuthHandler => {
  const env = getEnvVars();
  if (Helper.isLocalhost() && !env.useRemoteBackend) {
    return new LocalAuth();
  }
  return new ExternalAuth();
};

// Used in mock the auth package when running locally
class LocalAuth implements AuthHandler {
  signOutSession = (): Promise<void> => {
    return Promise.resolve();
  };

  signIn = (): Promise<void> => {
    return Promise.resolve();
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getCurrentSession = (_options?: GetSessionOptions): Promise<AuthSession> => {
    return Promise.resolve({ accessToken: 'jwt' });
  };
}

// Used in production to auth with Amplify
export class ExternalAuth implements AuthHandler {
  signOutSession = async (): Promise<void> => {
    await configure();
    // Use global sign out to invalidate all tokens and clear Cognito's hosted UI session
    // This ensures the user sees the login page on next sign-in attempt
    await signOut({ global: true });
  };

  signIn = async (): Promise<void> => {
    await configure();
    await signInWithRedirect();
  };

  getCurrentSession = async (options?: GetSessionOptions): Promise<AuthSession | undefined> => {
    await configure();
    try {
      // Use forceRefresh to proactively refresh the token if requested
      // This ensures we get a fresh token rather than a potentially expired cached one
      const session = await fetchAuthSession({ forceRefresh: options?.forceRefresh ?? false });
      const accessToken = session.tokens?.accessToken?.toString();
      if (!accessToken) {
        logger.warn('Current user session is invalid');
        return undefined;
      }
      logger.debug('Current user session is valid');
      return { accessToken };
    } catch (err) {
      logger.error('Get current session failure', err);
      return undefined;
    }
  };
}

export const signOutSession = (): Promise<void> => {
  return getAuthHandler().signOutSession();
};

export const signIn = (): Promise<void> => {
  return getAuthHandler().signIn();
};

export const getCurrentSession = async (options?: GetSessionOptions): Promise<AuthSession | undefined> => {
  return getAuthHandler().getCurrentSession(options);
};

// Configure Amplify only once - subsequent calls are no-ops to preserve internal token state
export const configure = async (): Promise<void> => {
  // Skip if already configured to preserve Amplify's internal token management state
  if (isAmplifyConfigured) {
    logger.debug('Amplify already configured, skipping reconfiguration');
    return;
  }

  const config = await getAppConfig();
  if (!config) {
    logger.error('Unable to get application configuration');
  } else {
    const { hostname } = window.location;
    const settings = {
      region: 'XX-XXXX-X',
      userPoolId: 'XX-XXXX-X_abcd1234',
      userPoolClientId: 'a1b2c3d4e5f6g7h8i9j0k1l2m3',
      loginWith: {
        oauth: {
          domain: hostname,
          scopes: ['openid', 'email', 'profile'],
          redirectSignIn: [window.location.origin],
          redirectSignOut: [window.location.origin],
          responseType: 'code' as const,
        },
      },
    };
    settings.region = config.region;
    settings.userPoolId = config.poolId;
    settings.userPoolClientId = config.clientId;
    settings.loginWith.oauth.domain = config.domain;

    // Configure amplify if running in the cloud
    Amplify.configure({
      Auth: {
        Cognito: settings,
      },
      API: {
        GraphQL: {
          endpoint: `https://${hostname}/graphql`,
          defaultAuthMode: 'userPool',
        },
      },
    });

    isAmplifyConfigured = true;
    logger.info('Amplify configured successfully');
  }
};

// Attach custom headers to graphql requests for API auth
export const customGraphQLHeaders = async (): Promise<Headers> => {
  // Wait for any ongoing CSRF token refresh to complete before getting headers
  await waitForCsrfRefresh();

  // Get current session - this may trigger Amplify to refresh the access token
  const session = await getAuthHandler().getCurrentSession();
  const accessToken = session?.accessToken || '';

  // Detect if the access token changed (was refreshed by Amplify)
  // If so, we need to refresh the CSRF token before proceeding
  const tokenChanged = accessToken && lastKnownAccessToken && accessToken !== lastKnownAccessToken;

  // IMPORTANT: Update lastKnownAccessToken BEFORE calling SaveCsrfToken to prevent deadlock.
  // SaveCsrfToken() calls customGraphQLHeaders() to get headers, so if we don't update
  // the token first, that nested call would also detect a "change" and try to refresh CSRF again.
  if (accessToken) {
    lastKnownAccessToken = accessToken;
  }

  if (tokenChanged) {
    logger.info('Access token changed, refreshing CSRF token before proceeding');
    await SaveCsrfToken();
  }

  const csrfToken = GetCsrfToken() || '';
  return {
    Authorization: `Bearer ${accessToken}`,
    'x-auth-subject': 'token',
    [CSRF_HEADER]: csrfToken,
  };
};

// Clear the browser of any auth session (auth in local storage, session storage, etc.)
export const RemoveBrowserAuthSession = (): void => {
  RemoveCsrfToken();

  for (let i = localStorage.length - 1; i >= 0; i--) {
    const k = localStorage.key(i);
    if (k && k.toLowerCase().startsWith('cognito')) {
      localStorage.removeItem(k);
    }
  }
};

// Type for the auth event listener cleanup function
export type AuthListenerCleanup = () => void;

// Callback type for when authentication fails and re-auth is needed
export type OnAuthFailureCallback = () => void;

// Callback type for when token refresh succeeds
export type OnTokenRefreshCallback = () => void;

// Options for setting up the auth listener
export interface AuthListenerOptions {
  onAuthFailure: OnAuthFailureCallback;
  onTokenRefresh?: OnTokenRefreshCallback;
  onSignOut?: OnSignOutCallback;
}

// Callback type for when sign out completes
export type OnSignOutCallback = () => void;

/**
 * Set up Hub listener for Amplify auth events to detect token refresh failures
 * and handle re-authentication automatically.
 *
 * @param options - Callbacks for auth events
 * @param options.onAuthFailure - Callback to execute when authentication fails (e.g., redirect to sign-in)
 * @param options.onTokenRefresh - Callback to execute when token is successfully refreshed (e.g., refresh CSRF token)
 * @param options.onSignOut - Callback to execute when user signs out (do NOT trigger re-auth)
 * @returns Cleanup function to remove the listener
 */
export const setupAuthListener = (options: AuthListenerOptions): AuthListenerCleanup => {
  const { onAuthFailure, onTokenRefresh, onSignOut } = options;
  const env = getEnvVars();
  // Don't set up listener for localhost without remote backend
  if (Helper.isLocalhost() && !env.useRemoteBackend) {
    logger.debug('Skipping auth listener setup for localhost');
    return () => {};
  }

  logger.info('Setting up Amplify Hub auth listener');

  const hubListenerCleanup = Hub.listen('auth', ({ payload }) => {
    switch (payload.event) {
      case 'tokenRefresh':
        logger.info('Access token refreshed successfully');
        // Trigger callback to refresh CSRF token or other dependent tokens
        if (onTokenRefresh) {
          onTokenRefresh();
        }
        break;
      case 'tokenRefresh_failure':
        logger.warn('Token refresh failed, triggering re-authentication');
        onAuthFailure();
        break;
      case 'signedOut':
        // User intentionally signed out - do NOT trigger re-auth flow
        // This would cause an immediate re-login since Cognito may still have a valid session
        logger.info('User signed out successfully');
        RemoveBrowserAuthSession();
        if (onSignOut) {
          onSignOut();
        }
        break;
      default:
        logger.debug('Auth event received', { event: payload.event });
    }
  });

  return hubListenerCleanup;
};
